import { z } from "zod";

// 基础 Schema
const BaseWordItemSchema = z.object({
  word: z.string(),
  correctAnswer: z.string(),
});

// 带判别字段的子类 Schema
const WordSelectionItemSchema = BaseWordItemSchema.extend({
  type: z.literal("selection"),
  options: z.array(z.string()),
});

const FillInBlankItemSchema = BaseWordItemSchema.extend({
  type: z.literal("fillInBlank"),
  sentence: z.string(),
  translation: z.string(),
  hint: z.string().optional(),
  info: z.string().optional(),
});

// 联合类型 Schema
const WordItemSchema = z.discriminatedUnion("type", [
  WordSelectionItemSchema,
  FillInBlankItemSchema,
]);

// 定义单一类型库的 schema
const WordSelectionLibrarySchema = z.array(WordSelectionItemSchema);
const FillInBlankLibrarySchema = z.array(FillInBlankItemSchema);

export const WordSelectionLibrariesSchema = z.record(
  z.string(),
  WordSelectionLibrarySchema
);

export const FillInBlankLibrariesSchema = z.record(
  z.string(),
  FillInBlankLibrarySchema
);

// 定义库条目，可以是选择题库或填空题库
const LibraryEntrySchema = z.union([
  WordSelectionLibrarySchema,
  FillInBlankLibrarySchema,
]);

// 定义整个词库集合
export const WordItemsLibrariesSchema = z.record(
  z.string(),
  LibraryEntrySchema
);

// 导出类型
export type BaseWordItem = z.infer<typeof BaseWordItemSchema>;
export type WordSelectionItem = z.infer<typeof WordSelectionItemSchema>;
export type FillInBlankItem = z.infer<typeof FillInBlankItemSchema>;
export type WordItem = z.infer<typeof WordItemSchema>;
export type WordSelectionLibrary = z.infer<typeof WordSelectionLibrarySchema>;
export type WordSelectionLibraries = z.infer<
  typeof WordSelectionLibrariesSchema
>;
export type FillInBlankLibrary = z.infer<typeof FillInBlankLibrarySchema>;
export type FillInBlankLibraries = z.infer<typeof FillInBlankLibrariesSchema>;
export type LibraryEntry = z.infer<typeof LibraryEntrySchema>;
export type WordItemsLibraries = z.infer<typeof WordItemsLibrariesSchema>;

// 类型守卫函数
export function isWordSelectionItem(item: WordItem): item is WordSelectionItem {
  return item.type === "selection";
}

export function isFillInBlankItem(item: WordItem): item is FillInBlankItem {
  return item.type === "fillInBlank";
}

export function assertWordSelectionItems(
  items: (WordSelectionItem | FillInBlankItem)[] | null
): WordSelectionItem[] {
  // Check if items is null
  if (items === null) {
    // throw new Error("No library items found");
    return [];
  }

  // Check if all items are WordSelectionItems
  const isWordSelectionItems = items.every(
    (item) => WordSelectionItemSchema.safeParse(item).success
  );

  if (!isWordSelectionItems) {
    throw new Error("The selected library is not a word selection library");
  }

  return items as WordSelectionItem[];
}

// 检测原始JSON对象类型的函数
function detectItemType(item: any): "selection" | "fillInBlank" {
  if ("options" in item && Array.isArray(item.options)) {
    return "selection";
  }
  if ("sentence" in item && typeof item.sentence === "string") {
    return "fillInBlank";
  }
  // 默认假设是选择题（因为你的示例中都是选择题）
  return "selection";
}

/**
 * 将混合词项数组过滤成选择题数组和填空题数组
 * @param items 混合词项数组
 * @returns 包含选择题数组和填空题数组的元组
 */
export function split(
  items: WordItem[]
): [WordSelectionItem[], FillInBlankItem[]] {
  const selectionItems: WordSelectionItem[] = [];
  const fillInBlankItems: FillInBlankItem[] = [];

  for (const item of items) {
    if (isWordSelectionItem(item)) {
      selectionItems.push(item);
    } else if (isFillInBlankItem(item)) {
      fillInBlankItems.push(item);
    }
  }

  return [selectionItems, fillInBlankItems];
}

export type GameMode =
  | "matching"
  | "sequential"
  | "allTogether"
  | "fillInBlank";

export interface GameState {
  currentMode: GameMode;
  selectedLibrary: string;
  activeGameLibrary: string;
  activeGameWords: WordItem[];
  wrongAnswers: WordItem[];
  elapsedTime: number;
  isTimerRunning: boolean;
  gameSessionId: number; // 添加游戏会话ID
}

export type CardType = "word" | "meaning";

export interface MatchingPair {
  type: CardType;
  content: string;
  id: string;
}
